import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Box, Button, Typography, alpha } from '@mui/material';
import {
  Edit          as EditIcon,
  Archive       as ArchiveIcon,
  LocalShipping as ParcelIcon,
  Person        as PersonIcon,
  Description   as DescriptionIcon,
  Notes         as NotesIcon,
  QrCode        as TrackingIcon,
  LocationOn    as LocationIcon,
  Phone         as PhoneIcon,
  Business      as BusinessIcon,
  CalendarToday as CalendarIcon,
  Event         as EventIcon,
  Assignment    as ReceivedIcon,
  Inventory     as ReceiverIcon,
  Category      as CategoryIcon,
} from '@mui/icons-material';
import { format, isValid } from 'date-fns';
import Header              from '../../common/Header';
import Loading             from '../../common/Loading';
import FeedbackMessage     from '../../common/FeedbackMessage';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import api                 from '../../../services/api';
import { useAuth }         from '../../../contexts/AuthContext';
import { canModifyParcel, canArchiveParcel } from '../../../utils/parcelPermissions';
import { resolveUserId, userDetailsPath } from '../../../utils/userDetailsLink';
import { SUCCESS }         from '../../common/SaaSTokens';
import { injectDetailsPageStyles } from '../../common/DetailsPagePrimitives';
import SystemInformationSection    from '../../common/SystemInformationSection';
import { formatSystemDateTime } from '../../../utils/systemDateFormat';


// ─── Design tokens ────────────────────────────────────────────────────────────
const NEON      = '#38BDF8';
const NEON2     = '#818CF8';
const NEON3     = '#34D399';
const SURFACE_MID = '#0a1628';
const SURFACE_UP  = '#0f1f38';
const TEXT_BODY   = '#E2E8F0';
const TEXT_MUTED  = '#64748B';

// ─── MetricCard — identical to DocumentDetails ────────────────────────────────
const MetricCard = ({ label, value, icon: Icon, color = NEON, sub }) => (
  <Box sx={{
    position: 'relative',
    borderRadius: '14px', px: 2.5, py: 2,
    background: `linear-gradient(145deg, ${alpha(color, 0.09)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)`,
    border: `1px solid ${alpha(color, 0.2)}`,
    boxShadow: `0 0 24px ${alpha(color, 0.07)}, inset 0 1px 0 ${alpha(color, 0.1)}`,
    overflow: 'hidden',
  }}>
    <Box sx={{ position: 'absolute', top: -16, right: -16, width: 70, height: 70, borderRadius: '50%', background: alpha(color, 0.08), filter: 'blur(18px)', pointerEvents: 'none' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
      {Icon && <Icon sx={{ fontSize: 13, color: alpha(color, 0.75) }} />}
      <Typography sx={{ fontSize: '0.67rem', fontFamily: "'DM Sans', sans-serif", color: TEXT_MUTED, letterSpacing: '0.09em', textTransform: 'uppercase', fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color, letterSpacing: 0.3, lineHeight: 1.15 }}>
      {value || '—'}
    </Typography>
    {sub && <Typography sx={{ fontSize: '0.7rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", mt: 0.4 }}>{sub}</Typography>}
  </Box>
);

// ─── Section — shared card shell ─────────────────────────────────────────────
const Section = ({ title, icon: Icon, accent = NEON, delay = 0, children }) => (
  <Box sx={{
    borderRadius: '16px',
    border: `1px solid ${alpha(accent, 0.14)}`,
    background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.75)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
    overflow: 'hidden',
    animation: `fadeUp 0.4s ease ${delay}s both`,
    '@keyframes fadeUp': {
      from: { opacity: 0, transform: 'translateY(10px)' },
      to:   { opacity: 1, transform: 'translateY(0)' },
    },
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.2,
      px: 2.5, py: 1.5,
      borderBottom: `1px solid ${alpha(accent, 0.1)}`,
      background: alpha(accent, 0.04),
    }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '7px',
        background: alpha(accent, 0.12),
        border: `1px solid ${alpha(accent, 0.22)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 13, color: accent }} />
      </Box>
      <Typography sx={{
        fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
        fontSize: '0.78rem', color: alpha(TEXT_BODY, 0.85),
        letterSpacing: '0.07em', textTransform: 'uppercase',
      }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

// ─── InfoRow ─────────────────────────────────────────────────────────────────
const InfoRow = ({ icon: Icon, label, value, color = NEON, emptyLabel = 'Not specified', fullWidth = false, onClick }) => {
  const display = value || emptyLabel;
  const isEmpty = !value;
  const isInteractive = Boolean(onClick && !isEmpty);
  return (
    <Box
      role={isInteractive ? 'link' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={isInteractive ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); }
      } : undefined}
      sx={{
      gridColumn: fullWidth ? '1 / -1' : undefined,
      display: 'flex', alignItems: 'center', gap: 1.5,
      py: 0.9, px: 1.2, borderRadius: '9px',
      cursor: isInteractive ? 'pointer' : 'default',
      transition: 'background 0.15s',
      '&:hover': isInteractive ? { background: alpha(color, 0.06) } : {},
    }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
        background: alpha(color, isEmpty ? 0.05 : 0.1),
        border: `1px solid ${alpha(color, isEmpty ? 0.1 : 0.18)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 14, color: isEmpty ? TEXT_MUTED : alpha(color, 0.85) }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.65rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: '0.88rem',
          color: isEmpty ? TEXT_MUTED : (isInteractive ? color : TEXT_BODY),
          fontStyle: isEmpty ? 'italic' : 'normal',
          fontFamily: "'DM Sans', sans-serif", fontWeight: isEmpty ? 400 : 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {display}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const STATUS_COLOR = {
  pending:   NEON2,
  received:  NEON,
  collected: NEON3,
  returned:  '#F87171',
  archived:  '#94A3B8',
};
const getStatusColor = (s) => STATUS_COLOR[s] ?? NEON;

const formatDate     = (val) => { if (!val) return null; const d = new Date(val); return isValid(d) ? format(d, 'EEEE, MMMM dd, yyyy') : null; };
const capitalize     = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '-';
const formatPerson   = (p)   => p ? p.name : null;
const resolveUpdater = (parcel) => parcel.last_updater?.name ?? '-';

// ─── ErrorState ───────────────────────────────────────────────────────────────
const ErrorState = ({ message }) => (
  <Container maxWidth="lg" sx={{ mt: 4 }}>
    <FeedbackMessage message={message} severity="error"
      sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', boxShadow: '0 4px 20px rgba(239,68,68,0.12)', color: '#fca5a5' }} />
  </Container>
);

// ─── ActionButtons ────────────────────────────────────────────────────────────
const ActionButtons = ({ canEdit, canArchive, onEdit, onArchive }) => {
  if (!canEdit && !canArchive) return null;
  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      {canEdit && (
        <Button startIcon={<EditIcon sx={{ fontSize: 16 }} />} onClick={onEdit}
          sx={{ borderRadius: '10px', px: 2.5, py: 0.7, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem', background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.6)}`, color: NEON, transition: 'all 0.2s ease', '&:hover': { background: alpha(NEON, 0.08), borderColor: NEON, boxShadow: `0 0 12px ${alpha(NEON, 0.2)}` } }}>
          Edit
        </Button>
      )}
      {canArchive && (
        <Button startIcon={<ArchiveIcon sx={{ fontSize: 16 }} />} onClick={onArchive}
          sx={{ borderRadius: '10px', px: 2.5, py: 0.7, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem', background: 'transparent', border: `1.5px solid ${alpha(NEON3, 0.6)}`, color: NEON3, transition: 'all 0.2s ease', '&:hover': { background: alpha(NEON3, 0.08), borderColor: NEON3, boxShadow: `0 0 12px ${alpha(NEON3, 0.2)}` } }}>
          Archive
        </Button>
      )}
    </Box>
  );
};

// ─── ParcelDetails ────────────────────────────────────────────────────────────
const ParcelDetails = () => {
  const { id }                = useParams();
  const navigate              = useNavigate();
  const { user: currentUser } = useAuth();

  const [parcel,           setParcel]           = useState(null);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState(null);
  const [successMessage,   setSuccessMessage]   = useState(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);

  const canEdit   = parcel && currentUser ? canModifyParcel(currentUser, parcel) : false;
  const canArchive = parcel && currentUser ? canArchiveParcel(currentUser, parcel) : false;

  useEffect(() => { injectDetailsPageStyles(); }, []);

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    fetchParcel();
  }, [currentUser, id, navigate]);

  const fetchParcel = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/parcels/${id}`);
      setParcel(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching parcel:', err);
      if (err.response?.status === 403) navigate('/dashboard');
      else setError('Failed to load parcel details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveParcel = async () => {
    try {
      const trackingNumber = parcel?.tracking_number ?? 'Parcel';
      await api.post(`/parcels/${id}/archive`);
      setParcel((prev) => (prev ? { ...prev, archived_at: new Date().toISOString() } : prev));
      setArchiveDialogOpen(false);
      setSuccessMessage(`Parcel "${trackingNumber}" has been archived.`);
    } catch (err) {
      console.error('Error archiving parcel:', err);
      setError(err?.response?.data?.message || 'Failed to archive parcel. Please try again.');
      setArchiveDialogOpen(false);
    }
  };

  if (!currentUser)     return <ErrorState message="Please log in to view this page." />;
  if (loading)          return <Loading message="Loading parcel details" size={56} variant="primary" />;
  if (error && !parcel) return <ErrorState message={error} />;
  if (!parcel)          return <ErrorState message="Parcel not found" />;

  const effectiveStatus = parcel.archived_at ? 'archived' : parcel.status;
  const statusColor  = getStatusColor(effectiveStatus);
  const hasReceiver  = Boolean(parcel.receiver);

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>

      <Header
        title="Parcel Details"
        subtitle="View parcel information"
        icon={ParcelIcon}
        showBackButton
        onBackClick={() => navigate(-1)}
        actionButton={
          <ActionButtons
            canEdit={canEdit}
            canArchive={canArchive}
            onEdit={() => navigate(`/parcels/edit/${id}`)}
            onArchive={() => setArchiveDialogOpen(true)}
          />
        }
      />

      <FeedbackMessage message={error} severity="error" onClose={() => setError(null)}
        sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', boxShadow: '0 4px 20px rgba(239,68,68,0.12)', color: '#fca5a5', mb: 3 }} />
      <FeedbackMessage message={successMessage} severity="success" onClose={() => setSuccessMessage(null)}
        sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha(SUCCESS, 0.12), border: `1px solid ${alpha(SUCCESS, 0.25)}`, color: alpha(SUCCESS, 0.95), mb: 2 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* ── Section 1: Parcel — hero style matching DocumentDetails ─────── */}
        <Box sx={{
          borderRadius: '16px',
          border: `1px solid ${alpha(NEON, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(SURFACE_UP, 0.9)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
          p: { xs: 2, md: 2.5 },
          animation: 'fadeUp 0.35s ease both',
          '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(10px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
        }}>
          {/* Title row */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: '13px', flexShrink: 0,
                background: `linear-gradient(135deg, ${alpha(NEON2, 0.25)}, ${alpha(NEON, 0.15)})`,
                border: `2px solid ${alpha(NEON2, 0.3)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <ParcelIcon sx={{ fontSize: 22, color: NEON2 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: TEXT_BODY, letterSpacing: 0.3, lineHeight: 1.15 }}>
                  {parcel.tracking_number || 'No Tracking Number'}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>
                  {parcel.location || 'No location set'}
                </Typography>
              </Box>
            </Box>

            {/* Status badge — pill identical to DocumentDetails visibility badge */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.7,
              px: 1.4, py: 0.5, borderRadius: '20px',
              background: alpha(statusColor, 0.1),
              border: `1px solid ${alpha(statusColor, 0.3)}`,
              alignSelf: 'flex-start',
            }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: statusColor }} />
              <Typography sx={{ fontSize: '0.75rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: statusColor, letterSpacing: '0.05em' }}>
                {capitalize(effectiveStatus)}
              </Typography>
            </Box>
          </Box>

          {/* Metric cards grid — 4 columns like DocumentDetails */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            <MetricCard label="Status"       value={capitalize(effectiveStatus)}                  icon={ParcelIcon}   color={statusColor} />
            <MetricCard label="Parcel Type"  value={parcel.parcel_type ? capitalize(parcel.parcel_type) : null} icon={CategoryIcon} color={NEON2} />
            <MetricCard label="Location"     value={parcel.location}                               icon={LocationIcon} color={NEON} />
            <MetricCard label="Penalty Date" value={formatDate(parcel.penalty_date)}               icon={EventIcon}    color={NEON3} />
          </Box>
        </Box>

        {/* ── Section 2 & 3: Sender + Receiver side-by-side ───────────────── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>

        {/* Sender */}
        <Section title="Sender" icon={BusinessIcon} accent={NEON2} delay={0.06}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <InfoRow icon={BusinessIcon}   label="Sender Name"  value={formatPerson(parcel.buyer)} color={NEON2} />
            <InfoRow icon={PhoneIcon}      label="Phone Number" value={parcel.phone_number}         color={NEON2} />
            {parcel.description && (
              <InfoRow icon={DescriptionIcon} label="Description" value={parcel.description} color={NEON2} />
            )}
          </Box>
        </Section>

        {/* Receiver */}
        <Section title="Receiver" icon={ReceiverIcon} accent={NEON3} delay={0.12}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <InfoRow
              icon={PersonIcon}
              label="Receiver Name"
              value={formatPerson(parcel.receiver)}
              color={NEON3}
              emptyLabel="Not yet assigned"
              onClick={resolveUserId(parcel.receiver) ? () => navigate(userDetailsPath(resolveUserId(parcel.receiver))) : undefined}
            />
            <InfoRow
              icon={ReceivedIcon}
              label="Collected Date"
              value={parcel.status === 'collected' ? formatDate(parcel.received_date) : null}
              color={NEON3}
              emptyLabel="Not yet collected"
            />
            {parcel.additional_note && (
              <InfoRow icon={NotesIcon} label="Additional Notes" value={parcel.additional_note} color={NEON3} />
            )}
          </Box>

          {!hasReceiver && (
            <Box sx={{ mt: 1.5, mx: 1.2, px: 2, py: 1.2, borderRadius: '10px', background: alpha(NEON3, 0.04), border: `1px dashed ${alpha(NEON3, 0.18)}`, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReceiverIcon sx={{ fontSize: 15, color: alpha(NEON3, 0.5) }} />
              <Typography sx={{ fontSize: '0.78rem', color: alpha(NEON3, 0.6), fontFamily: "'DM Sans', sans-serif", fontStyle: 'italic' }}>
                No receiver assigned yet. Update the parcel status to Collected to assign one.
              </Typography>
            </Box>
          )}
        </Section>

        </Box>

        {/* ── Section 4: System Information ────────────────────────────────── */}
        <SystemInformationSection
          createdBy={parcel.creator?.name ?? '-'}
          createdAt={formatSystemDateTime(parcel.created_at)}
          updatedBy={resolveUpdater(parcel)}
          updatedAt={formatSystemDateTime(parcel.updated_at)}
          delay={0.18}
          accentColor={NEON}
          surfaceUp={SURFACE_UP}
          surfaceMid={SURFACE_MID}
          textBody={TEXT_BODY}
          textMuted={TEXT_MUTED}
        />

      </Box>

      <ConfirmDeleteDialog
        open={archiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
        onConfirm={handleArchiveParcel}
        title="Archive Parcel"
        itemType="parcel"
        itemName={parcel.tracking_number}
        description="The parcel will be archived and remain in the system for history and audit purposes."
        confirmLabel="Archive Parcel"
        actionVerb="archive"
      />
    </Container>
  );
};

export default ParcelDetails;
